import { api } from "@/api";
import { MutationConfig, MutationResult } from "@/shared/types/base";
import { sleep } from "@/shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";
import { VentaRequest } from "@/features/inventario/ventas/api/add-venta";
import { PayVentaRequest } from "./pay-venta";
import { CondicionPagoDto } from "@/features/facturacion-electronica/utils/condicion-pago-mapper";

export interface FacturaElectronicaResponse {
  cdc: string;
  numeroDE: string;
  kudeBase64?: string;
}

export interface PagoFEResponseData {
  pagoId: string;
  ventaId: string;
  fecha?: string;
  facturaElectronica?: FacturaElectronicaResponse;
}

export interface PagoFEResponse {
  success: boolean;
  message: string;
  data: PagoFEResponseData;
}

export interface PagoFERequest {
  pago: PayVentaRequest;
  dEst: string;
  dPunExp: string;
  iTipEmi: number;
  iTipTra: number;
  iIndPres: number;
  iTImp: number;
  monedaId?: string;
  dCondTiCam: number;
  dTiCam?: number;
  dDesIndPres?: string;
  dInfAdic?: string;
  condicionPago: CondicionPagoDto;
}

export interface PayVentaFERequest {
  pago: PayVentaRequest;
  dEst: string;
  dPunExp: string;
  iTipEmi: number;
  iIndPres: number;
  iTImp: number;
  monedaId?: string;
  dCondTiCam: number;
  dTiCam?: number;
  condicionPago: CondicionPagoDto;
}

export interface CreateAndPayVentaConFERequest {
  ventaRequest: VentaRequest;
  pagoFERequest: PagoFERequest;
}

export async function createAndPayVentaConFE(request: CreateAndPayVentaConFERequest): Promise<PagoFEResponse> {
  await sleep(1000);
  const response = await api.post<PagoFEResponse>("caja_actual/pay-fe", request, { headers: { "X-Venta-Token": request.ventaRequest.token } });

  const apiData = response.data;
  const feApi = apiData.data?.facturaElectronica;

  return {
    success: apiData.success,
    message: apiData.message,
    data: {
      pagoId: apiData.data?.pagoId || "",
      ventaId: apiData.data?.ventaId || "",
      fecha: apiData.data?.fecha,
      facturaElectronica: feApi
        ? {
          cdc: feApi.cdc || "",
          numeroDE: feApi.numeroDE || "",
          kudeBase64: feApi.kudeBase64,
        }
        : undefined,
    }
  };
}

export function useCreateAndPayVentaConFEMutation(mutationConfig: MutationConfig<PagoFEResponse, CreateAndPayVentaConFERequest> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    ...mutationConfig,
    mutationFn: createAndPayVentaConFE,
    onSuccess: (...args) => {
      mutationConfig.onSuccess && mutationConfig.onSuccess(...args);
      queryClient.invalidateQueries({ queryKey: ["ventas", "unpaid"] });
    },
  })
}

export interface CreateAndPayVentaConFEMutationProps {
  mutationConfig?: MutationConfig<PagoFEResponse, CreateAndPayVentaConFERequest>
  children?: (mutationResult: MutationResult<PagoFEResponse, CreateAndPayVentaConFERequest>) => ReactNode
}

export function CreateAndPayVentaConFEMutation({
  mutationConfig,
  children
}: CreateAndPayVentaConFEMutationProps) {
  const mutationResult = useCreateAndPayVentaConFEMutation(mutationConfig);
  return children && children(mutationResult);
}

export async function payVentaConFE(ventaId: string, request: PayVentaFERequest): Promise<PagoFEResponse> {
  await sleep(1000);
  const response = await api.post<PagoFEResponse>(`caja_actual/pay-fe/${ventaId}`, request);

  const apiData = response.data;
  const feApi = apiData.data?.facturaElectronica;

  return {
    success: apiData.success,
    message: apiData.message,
    data: {
      pagoId: apiData.data?.pagoId || "",
      ventaId: apiData.data?.ventaId || "",
      fecha: apiData.data?.fecha,
      facturaElectronica: feApi
        ? {
          cdc: feApi.cdc || "",
          numeroDE: feApi.numeroDE || "",
          kudeBase64: feApi.kudeBase64,
        }
        : undefined,
    },
  };
}

export function usePayVentaConFEMutation(ventaId: string, mutationConfig: MutationConfig<PagoFEResponse, PayVentaFERequest> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    ...mutationConfig,
    mutationFn: request => payVentaConFE(ventaId, request),
    onSuccess: (...args) => {
      mutationConfig.onSuccess && mutationConfig.onSuccess(...args);
      queryClient.invalidateQueries({ queryKey: ["ventas", "unpaid"] });
    },
  })
}
